import { css } from "@emotion/react"
import { CurrencyInput } from "source/giving/components/CurrencyInput"
import { CustomSelect } from "source/giving/components/CustomSelect"
import { Field } from "source/giving/components/Field"
import { Icon } from "source/shared/components"
import { BLANK_FUND_ID } from "../constants"
import { extractMessagesFrom } from "../stores/validations"
import { Designation, Fund, ValidationErrors } from "../types"
import { getFieldId } from "./FundSelection"

type DesignationRowProps = Designation & {
  errors: ValidationErrors
  funds: Fund[]
  index: number
  onFundChange: (fundId: string) => void
  onAmountChange: (amount: string) => void
  onRemove: () => void
  onFundBlur?: () => void
  onAmountBlur?: () => void
}

export function DesignationRow({
  amount,
  errors: validationErrors,
  fundId,
  funds,
  index,
  onFundChange,
  onAmountChange,
  onRemove,
  onFundBlur,
  onAmountBlur,
}: DesignationRowProps) {
  const FIELD_NAMES = {
    FUND: getFieldId("fund", index),
    AMOUNT: getFieldId("amount", index),
  }
  const humanIndex = index + 1

  const fieldsetErrors = extractMessagesFrom(
    validationErrors,
    `designation_${fundId}`,
  )
  const fundErrors = extractMessagesFrom(
    validationErrors,
    `designation_${fundId}_fund`,
  )
  const amountErrors = extractMessagesFrom(
    validationErrors,
    `designation_${fundId}_amount`,
  )

  const handleFundChange = (fundId: string) => {
    onFundChange(fundId)

    // re-focus after change (updated options cause a re-render)
    requestAnimationFrame(() => {
      const element = document.getElementById(FIELD_NAMES.FUND)
      if (element) {
        element.focus()
      }
    })
  }

  return (
    <Field errors={fieldsetErrors}>
      {({ id, isValid, errorId }) => (
        <fieldset
          id={id}
          className="d-f ai-c g-1"
          aria-invalid={!isValid}
          aria-describedby={errorId}
          data-testid={`designation-${index}-fieldset`}
        >
          <legend className="visually-hidden">
            Fund designation {humanIndex}
          </legend>
          <label className="visually-hidden" htmlFor={FIELD_NAMES.FUND}>
            Fund {humanIndex}
          </label>
          <CustomSelect
            className="f-2"
            id={FIELD_NAMES.FUND}
            value={fundId}
            onChange={handleFundChange}
            onBlur={onFundBlur}
            aria-invalid={fundErrors.length > 0}
            placeholder="Choose a fund"
            data-testid={`designation-${index}-fund-select`}
          >
            {fundId === BLANK_FUND_ID && (
              <CustomSelect.Option value={BLANK_FUND_ID}>
                Choose a fund
              </CustomSelect.Option>
            )}
            {funds.map((fund) => (
              <CustomSelect.Option
                key={fund.id}
                value={fund.id}
                description={fund.description}
              >
                {fund.name}
              </CustomSelect.Option>
            ))}
          </CustomSelect>

          <label className="visually-hidden" htmlFor={FIELD_NAMES.AMOUNT}>
            Amount for fund {humanIndex}
          </label>
          <CurrencyInput
            className="f-1"
            id={FIELD_NAMES.AMOUNT}
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            onBlur={onAmountBlur}
            aria-invalid={amountErrors.length > 0}
            data-testid={`designation-${index}-amount-input`}
          />

          <button
            className="btn minor-btn badge-btn p-0"
            css={css`
              aspect-ratio: 1;
            `}
            type="button"
            onClick={onRemove}
            aria-label={`Remove fund ${humanIndex}}`}
            data-testid={`designation-${index}-remove-button`}
          >
            <Icon symbol="general#minus" aria-hidden="true" />
          </button>
        </fieldset>
      )}
    </Field>
  )
}
